// Global theme JavaScript

/* ===== Drawers (menu + cart) ===== */
const Drawers = {
  open(name) {
    const drawer = document.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return false;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('has-drawer');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    drawer.querySelector('[data-drawer-close].header__icon')?.focus();
    return true;
  },
  close(drawer) {
    drawer.classList.remove('is-open');
    document.body.classList.remove('has-drawer');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { drawer.hidden = true; }, 300);
  },
};

/* ===== Cart ===== */
const Cart = {
  drawer: () => document.getElementById('CartDrawer'),

  formatMoney(cents) {
    const format = this.drawer()?.dataset.moneyFormat || 'AED {{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withCommas;
      if (key === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
      return `${withCommas}.${decimals}`;
    });
  },

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  },

  async refresh() {
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    this.render(cart);
    return cart;
  },

  render(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
    });

    const drawer = this.drawer();
    if (!drawer) return;

    const list = drawer.querySelector('[data-cart-items]');
    const empty = drawer.querySelector('[data-cart-empty]');
    const foot = drawer.querySelector('[data-cart-foot]');
    empty.hidden = cart.item_count > 0;
    foot.hidden = cart.item_count === 0;
    drawer.querySelector('[data-cart-subtotal]').textContent = this.formatMoney(cart.total_price);

    list.innerHTML = cart.items.map((item) => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__media">
          ${item.image ? `<img src="${item.image}${item.image.includes('?') ? '&' : '?'}width=200" alt="${this.escape(item.product_title)}" width="80" height="80" loading="lazy">` : ''}
        </a>
        <div class="cart-item__info">
          <a href="${item.url}" class="cart-item__title">${this.escape(item.product_title)}</a>
          ${item.product_has_only_default_variant ? '' : `<p class="cart-item__variant">${this.escape(item.variant_title)}</p>`}
          <div class="cart-item__row">
            <div class="qty">
              <button type="button" aria-label="Decrease quantity" data-qty="${item.quantity - 1}">−</button>
              <span>${item.quantity}</span>
              <button type="button" aria-label="Increase quantity" data-qty="${item.quantity + 1}">+</button>
            </div>
            <strong>${this.formatMoney(item.final_line_price)}</strong>
          </div>
        </div>
        <button type="button" class="cart-item__remove" aria-label="Remove ${this.escape(item.product_title)}" data-qty="0">×</button>
      </div>`).join('');

    const threshold = Number(drawer.dataset.freeShipping || 0);
    const meter = drawer.querySelector('[data-shipping-meter]');
    if (meter && threshold > 0) {
      const remaining = threshold - cart.total_price;
      meter.querySelector('[data-shipping-bar]').style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
      meter.querySelector('[data-shipping-text]').innerHTML = remaining > 0
        ? `You're <strong>${this.formatMoney(remaining)}</strong> away from <strong>FREE delivery</strong> 🚚`
        : '🎉 You unlocked <strong>FREE delivery!</strong>';
    }

    // Upsell + recommendations: hide anything already in the cart; mark the deal as added
    const extras = drawer.querySelector('[data-cart-extras]');
    if (extras) {
      const ids = new Set(cart.items.map((item) => item.product_id));
      let visible = 0;
      extras.querySelectorAll('[data-upsell]').forEach((el) => {
        const inCart = ids.has(Number(el.dataset.productId));
        el.hidden = inCart;
        if (!inCart) visible += 1;
        const claim = el.querySelector('[data-quick-add]')?.closest('.cart-deal__claim');
        if (claim) claim.classList.toggle('is-added', inCart);
      });
      extras.hidden = cart.item_count === 0 || visible === 0;
    }
  },

  async add(variantId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    await this.refresh();
  },

  async change(key, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    this.render(await response.json());
  },
};

/* ===== Slideshow ===== */
function initSlideshow(root) {
  const slides = [...root.querySelectorAll('[data-slide]')];
  const dots = [...root.querySelectorAll('[data-dot]')];
  if (slides.length < 2) return;

  let index = 0;
  let timer;
  const delay = Number(root.dataset.autoplay) * 1000;

  const show = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      slide.toggleAttribute('inert', i !== index);
    });
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  };
  const play = () => {
    clearInterval(timer);
    if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      timer = setInterval(() => show(index + 1), delay);
    }
  };

  root.querySelector('[data-prev]')?.addEventListener('click', () => { show(index - 1); play(); });
  root.querySelector('[data-next]')?.addEventListener('click', () => { show(index + 1); play(); });
  dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.dot)); play(); }));
  root.addEventListener('mouseenter', () => clearInterval(timer));
  root.addEventListener('mouseleave', play);

  // Basic swipe support on touch screens
  let startX = null;
  root.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
  root.addEventListener('touchend', (e) => {
    if (startX === null) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 40) { show(index + (dx < 0 ? 1 : -1)); play(); }
    startX = null;
  });

  show(0);
  play();
}

/* ===== Tabs ===== */
function initTabs(root) {
  const tabs = [...root.querySelectorAll('[data-tab]')];
  const panels = [...root.querySelectorAll('[data-panel]')];
  tabs.forEach((tab, i) => tab.addEventListener('click', () => {
    tabs.forEach((t, j) => {
      t.classList.toggle('is-active', i === j);
      t.setAttribute('aria-selected', i === j ? 'true' : 'false');
    });
    panels.forEach((panel, j) => { panel.hidden = i !== j; });
  }));
}

/* ===== Product landing page ===== */
function initPdp(root) {
  const variants = JSON.parse(root.querySelector('[data-pdp-variants]')?.textContent || '[]');
  const track = root.querySelector('[data-gallery-track]');
  const thumbs = [...root.querySelectorAll('[data-thumb]')];
  const sticky = root.querySelector('[data-sticky]');

  const goToSlide = (index) => {
    const slide = track?.children[index];
    if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };
  thumbs.forEach((thumb) => thumb.addEventListener('click', () => goToSlide(Number(thumb.dataset.thumb))));
  track?.addEventListener('scroll', () => {
    const index = Math.round(track.scrollLeft / track.clientWidth);
    thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }, { passive: true });

  const select = (variant) => {
    if (!variant) return;
    root.querySelectorAll('[data-bundle-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.querySelector('input').value === String(variant.id));
    });

    root.querySelectorAll('[data-atc-price]').forEach((el) => { el.textContent = variant.price; });
    root.querySelectorAll('[data-atc-compare]').forEach((el) => { el.textContent = variant.compare; });
    root.querySelectorAll('[data-sticky-price]').forEach((el) => { el.textContent = variant.price; });
    root.querySelectorAll('[data-sticky-title]').forEach((el) => { el.textContent = variant.title; });
    root.querySelectorAll('[data-buy-now], [data-cod-trigger], button[name="add"]').forEach((button) => { button.disabled = !variant.available; });

    const gift = root.querySelector('[data-gift]');
    if (gift) gift.classList.toggle('is-unlocked', variant.index >= Number(gift.dataset.unlockFrom));

    root.querySelectorAll('[data-include]').forEach((item) => {
      item.classList.toggle('is-in', variant.index >= Number(item.dataset.from));
    });

    // Real stock only: show the count when Shopify tracks inventory and it is below the threshold
    const stock = root.querySelector('[data-stock]');
    if (stock) {
      const text = stock.querySelector('[data-stock-text]');
      const low = variant.management === 'shopify' && variant.quantity > 0 && variant.quantity <= Number(stock.dataset.threshold);
      stock.classList.toggle('is-low', low);
      text.textContent = low ? text.dataset.low.replace('[count]', variant.quantity) : text.dataset.ok;
    }

    if (variant.media && track) {
      const index = [...track.children].findIndex((slide) => slide.dataset.mediaId === String(variant.media));
      if (index >= 0) goToSlide(index);
    }
  };

  root.addEventListener('change', (event) => {
    if (event.target.matches('input[name="id"][type="radio"]')) {
      select(variants.find((v) => String(v.id) === event.target.value));
    }
  });

  // "Buy it now": add the selected offer, then go straight to checkout (skips the cart drawer)
  const form = root.querySelector('form');
  const buyNow = async (btn) => {
    const idInput = form && (form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]'));
    if (!idInput) return;
    btn.classList.add('is-loading');
    btn.setAttribute('disabled', 'disabled');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: 1 }),
      });
      if (!res.ok) throw new Error('add failed');
      window.location.href = '/checkout';
    } catch (error) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('disabled');
    }
  };
  root.querySelectorAll('[data-buy-now]').forEach((btn) => btn.addEventListener('click', () => buyNow(btn)));

  // COD-only pages: the sticky button opens the COD app's order form
  root.querySelectorAll('[data-cod-trigger]').forEach((btn) => btn.addEventListener('click', () => {
    const cod = document.querySelector('#rsi_buy_now_button');
    if (cod) cod.click();
    else document.querySelector('#rsi-button-hook')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }));

  // Sticky bar shows once the main buy button has scrolled out of view (scrolling up or down)
  const atc = root.querySelector('[data-atc]') || root.querySelector('#rsi-button-hook');
  if (sticky && atc && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.querySelector('button').tabIndex = show ? 0 : -1;
      document.body.classList.toggle('has-sticky', show);
    }).observe(atc);
  }
}

// Courier cutoff in Dubai time (UTC+4, no DST). Orders and deliveries run Monday to Saturday.
const DUBAI_OFFSET = 4 * 36e5;
const DAY_MS = 864e5;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function deliveryInfo(now, cutoffHour) {
  const local = now + DUBAI_OFFSET;
  const today = Math.floor(local / DAY_MS) * DAY_MS;
  const isWorkday = (ms) => new Date(ms).getUTCDay() !== 0;
  let cutoff = today + cutoffHour * 36e5;
  while (cutoff <= local || !isWorkday(cutoff)) cutoff += DAY_MS;
  let delivery = Math.floor(cutoff / DAY_MS) * DAY_MS + DAY_MS;
  while (!isWorkday(delivery)) delivery += DAY_MS;
  return {
    msLeft: cutoff - local,
    cutoffDay: new Date(cutoff).getUTCDay(),
    deliveryDay: new Date(delivery).getUTCDay(),
    tomorrow: delivery - today === DAY_MS
  };
}

// Per-visitor offer: the window starts on the first visit and never restarts within 24h.
// The deadline is saved on the cart, so the order shows whether it qualified.
function initVisitorOffer(root) {
  const KEY = 'pr-offer-start';
  const urgent = root.querySelector('[data-delivery-urgent]');
  const urgentText = root.querySelector('[data-delivery-urgent-text]');
  const dayText = root.querySelector('[data-delivery-day]');
  let start = 0;
  try { start = Number(localStorage.getItem(KEY)) || 0; } catch (e) {}
  if (!start || Date.now() - start > DAY_MS) {
    start = Date.now();
    try { localStorage.setItem(KEY, String(start)); } catch (e) {}
  }
  const end = start + Number(root.dataset.window) * 6e4;
  if (end <= Date.now()) {
    dayText.textContent = root.dataset.expired;
    return;
  }

  const deadline = new Date(end).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', dateStyle: 'medium', timeStyle: 'short' });
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { 'Next-day offer until (Dubai)': deadline } })
  }).catch(() => {});

  const [before, after = ''] = root.dataset.day.split('[DAY]');
  const strong = document.createElement('strong');
  strong.textContent = root.dataset.tomorrow;
  dayText.replaceChildren(before, strong, after);

  const tick = () => {
    const seconds = Math.ceil((end - Date.now()) / 1000);
    if (seconds <= 0) {
      clearInterval(timer);
      urgent.hidden = true;
      dayText.textContent = root.dataset.expired;
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    urgentText.textContent = root.dataset.urgent.replace('[time]', `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`);
    urgent.hidden = false;
  };
  const timer = setInterval(tick, 1000);
  tick();
}

function initDelivery(root) {
  if (root.dataset.mode === 'visitor') return initVisitorOffer(root);
  const cutoffHour = Number(root.dataset.cutoff);
  const hourLabel = `${cutoffHour % 12 || 12} ${cutoffHour < 12 ? 'AM' : 'PM'}`;
  const urgent = root.querySelector('[data-delivery-urgent]');
  const urgentText = root.querySelector('[data-delivery-urgent-text]');
  const dayText = root.querySelector('[data-delivery-day]');

  const render = () => {
    const info = deliveryInfo(Date.now(), cutoffHour);
    const minutes = Math.ceil(info.msLeft / 6e4);
    urgentText.textContent = info.msLeft <= DAY_MS
      ? root.dataset.urgent.replace('[time]', `${Math.floor(minutes / 60)}h ${minutes % 60}m`)
      : root.dataset.later.replace('[day]', WEEKDAYS[info.cutoffDay]).replace('[hour]', hourLabel);
    urgent.hidden = false;

    // "Tomorrow" only when it is true; otherwise a neutral next-day line, never a weekday
    if (info.tomorrow) {
      const [before, after = ''] = root.dataset.day.split('[DAY]');
      const strong = document.createElement('strong');
      strong.textContent = root.dataset.tomorrow;
      dayText.replaceChildren(before, strong, after);
    } else {
      dayText.textContent = root.dataset.other;
    }
  };

  render();
  setInterval(render, 30000);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-slideshow]').forEach(initSlideshow);
  document.querySelectorAll('[data-tabs]').forEach(initTabs);
  document.querySelectorAll('[data-pdp]').forEach(initPdp);
  document.querySelectorAll('[data-delivery]').forEach(initDelivery);

  // Shadow on the sticky header once the page scrolls
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Fade sections in as they enter the viewport
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    document.querySelectorAll('.section').forEach((el) => {
      el.classList.add('reveal');
      observer.observe(el);
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-menu-open]')) {
      Drawers.open('menu');
      return;
    }
    if (event.target.closest('[data-cart-open]') && Cart.drawer()) {
      event.preventDefault();
      Drawers.open('cart');
      Cart.refresh();
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      Drawers.close(closer.closest('[data-drawer]'));
      return;
    }
    const qtyButton = event.target.closest('#CartDrawer [data-qty]');
    if (qtyButton) {
      qtyButton.disabled = true;
      Cart.change(qtyButton.closest('[data-key]').dataset.key, Math.max(0, Number(qtyButton.dataset.qty)));
      return;
    }

    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      quickAdd.disabled = true;
      quickAdd.classList.add('is-loading');
      Cart.add(quickAdd.dataset.variantId).finally(() => {
        quickAdd.disabled = false;
        quickAdd.classList.remove('is-loading');
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-drawer].is-open').forEach((drawer) => Drawers.close(drawer));
  });

  // Add to cart via the Ajax API, then open the cart drawer
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches('form[action$="/cart/add"]')) return;

    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    if (button) {
      button.setAttribute('disabled', 'disabled');
      button.classList.add('is-loading');
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error((await response.json()).description);
      await Cart.refresh();
      if (!Drawers.open('cart')) window.location.href = '/cart';
    } catch (error) {
      console.error('Add to cart failed:', error);
      form.submit(); // fall back to a normal form post
    } finally {
      if (button) {
        button.removeAttribute('disabled');
        button.classList.remove('is-loading');
      }
    }
  });
});
